import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Manages the push subscription lifecycle for the admin dashboard.
 * Returns { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest }.
 */
export default function usePushSubscription() {
  const supported = typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;

  const [permission, setPermission] = useState(
    supported ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch { /* ignore */ }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    // Register SW once. React StrictMode may double-invoke; register() is idempotent.
    navigator.serviceWorker.register("/sw.js").catch(() => {});
    refresh();
  }, [supported, refresh]);

  const subscribe = useCallback(async () => {
    if (!supported) {
      toast.error("Este navegador no soporta notificaciones push");
      return false;
    }
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Debes permitir las notificaciones en el navegador");
        return false;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { data: cfg } = await api.get("/push/vapid-key");
        if (!cfg.public_key) throw new Error("Push no está configurado en el servidor");
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(cfg.public_key),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notificaciones al celular activadas");
      return true;
    } catch (e) {
      toast.error(apiError(e) || e.message || "No se pudo activar");
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", {
          endpoint: sub.endpoint,
          keys: sub.toJSON().keys,
        }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast("Notificaciones desactivadas");
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Prueba enviada · ${data.delivered} dispositivo(s)`);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest };
}
